/**
 * Centralized Color Palette
 *
 * Single source of truth for all colors across the platform.
 * Used by components, status indicators, typography, and any UI element.
 *
 * Usage:
 * import { colors } from '@/lib/theme/colors';
 * style={{ color: colors.text.primary }}
 */

import { SIGNAL_TYPE_COLORS } from '@/constants/signalTypeCatalog';

export const colors = {
  /**
   * Primary Brand Colors
   */
  primary: {
    50: '#EFF6FF',
    100: '#DBEAFE',
    200: '#BFDBFE',
    300: '#93C5FD',
    400: '#60A5FA',
    500: '#006BFF',  // Main brand blue
    600: '#0056cc',
    700: '#004299',
    800: '#002d66',
    900: '#001933',
  },

  /**
   * Status Colors (Semantic)
   */
  status: {
    success: {
      light: '#EEFDF7',      // Pill/badge background
      lighter: '#ECFDF5',    // Icon container background
      base: '#10B981',       // Icon/primary color (emerald-500)
      dark: '#059669',       // emerald-600
    },
    error: {
      light: '#FFF4E1',      // Pill/badge background (soft orange)
      lighter: '#FEF2F2',    // Icon container background
      base: '#F97316',       // Icon/primary color (orange-500)
      dark: '#EA580C',       // orange-600
    },
    warning: {
      light: '#FEF3C7',      // Pill/badge background
      lighter: '#FFFBEB',    // Icon container background
      base: '#F59E0B',       // Icon/primary color (amber-500)
      dark: '#D97706',       // amber-600
    },
    info: {
      light: '#E0F2FE',      // Pill/badge background
      lighter: '#EFF6FF',    // Icon container background
      base: '#006BFF',       // Icon/primary color (primary blue)
      dark: '#0056cc',       // blue-600
    },
    neutral: {
      light: '#F1F5F9',      // Pill/badge background
      lighter: '#F8FAFC',    // Icon container background
      base: '#64748B',       // Icon/primary color (slate-500)
      dark: '#475569',       // slate-600
    },
  },

  /**
   * Text Colors
   */
  text: {
    primary: '#0F172A',      // slate-900 - Main headings, important text
    secondary: '#334155',    // slate-700 - Subheadings, labels
    tertiary: '#64748B',     // slate-500 - Helper text, descriptions
    quaternary: '#94A3B8',   // slate-400 - Placeholder, disabled
    muted: '#CBD5E1',        // slate-300 - Very subtle text
    inverse: '#FFFFFF',      // White text on dark backgrounds
  },

  /**
   * Background Colors
   */
  background: {
    primary: '#FFFFFF',      // Main background
    secondary: '#F8FAFC',    // slate-50 - Secondary background
    tertiary: '#F1F5F9',     // slate-100 - Card backgrounds
    hover: '#F1F5F9',        // slate-100 - Hover states
    selected: '#E2E8F0',     // slate-200 - Selected states
  },

  /**
   * Border Colors
   */
  border: {
    light: '#F1F5F9',        // slate-100 - Very subtle borders
    default: '#E2E8F0',      // slate-200 - Default borders
    medium: '#CBD5E1',       // slate-300 - Medium borders
    dark: '#94A3B8',         // slate-400 - Emphasized borders
  },

  /**
   * Interactive Colors (Buttons, Links, etc.)
   */
  interactive: {
    primary: '#006BFF',      // Primary action color
    primaryHover: '#0056cc', // Primary hover
    secondary: '#64748B',    // Secondary action
    secondaryHover: '#475569', // Secondary hover
    danger: '#EF4444',       // Destructive actions (red-500)
    dangerHover: '#DC2626',  // Destructive hover (red-600)
  },

  /**
   * Utility Colors
   */
  utility: {
    focus: '#006BFF',        // Focus ring color
    shadow: '#0F172A',       // Shadow color
    overlay: '#0F172A',      // Modal overlay
    divider: '#E2E8F0',      // Divider lines (slate-200)
  },

  /**
   * Data Visualization Colors (for charts, graphs)
   */
  data: {
    blue: '#3B82F6',
    green: '#10B981',
    yellow: '#F59E0B',
    red: '#EF4444',
    purple: '#8B5CF6',
    pink: '#EC4899',
    orange: '#F97316',
    teal: '#14B8A6',
  },

  /**
   * Special UI Colors
   */
  special: {
    // MarketBetter brand specific
    mbBlue: {
      50: '#EFF6FF',
      100: '#DBEAFE',
      500: '#094FFE',
      700: '#1D4ED8',
    },
    // Danger zones
    danger: {
      50: '#FEF2F2',
      100: '#FEE2E2',
      200: '#FECACA',
      500: '#EF4444',
      600: '#DC2626',
      700: '#B91C1C',
    },
  },

  /**
   * Dialer-specific Status Colors (Figma Design Spec)
   *
   * These colors match the exact Figma specification for the dialer/call UI:
   * - Text-only status pills without background/border
   * - Typography: 12px, weight 500, letter-spacing -0.24px
   */
  dialer: {
    // Answered/Success statuses - Figma: #3EA67E
    success: '#3EA67E',
    // No answer/Error statuses - Figma: #E72208
    error: '#E72208',
    // Ongoing/Active statuses - Same as primary blue
    active: '#094FFE',
    // Neutral/Pending statuses
    neutral: '#64748B',      // slate-500
    muted: '#9CA3AF',        // gray-400
  },

  /**
   * Signal Type Colors
   *
   * Used for signal type indicator dots/badges in the tasks list.
   * Each signal type has a distinctive color for quick visual recognition.
   */
  signals: {
    // One colour per signal type, from the signal-type registry
    // (constants/signalTypeCatalog.ts), which is where a type's colour is set.
    ...SIGNAL_TYPE_COLORS,

    // Fallback
    default: '#94A3B8',            // slate-400
  },

  /**
   * Workflow Status Colors
   *
   * Used for workflow status badges across the platform.
   * Each status has bg (background), text, and border variants.
   */
  workflow: {
    new: {
      bg: '#ECFDF5',               // emerald-50
      text: '#059669',             // emerald-600
      border: '#A7F3D0',           // emerald-200
    },
    in_progress: {
      bg: '#EFF6FF',               // blue-50
      text: '#2563EB',             // blue-600
      border: '#BFDBFE',           // blue-200
    },
    ready: {
      bg: '#ECFDF5',               // emerald-50
      text: '#059669',             // emerald-600
      border: '#A7F3D0',           // emerald-200
    },
    pending_review: {
      bg: '#FFFBEB',               // amber-50
      text: '#D97706',             // amber-600
      border: '#FDE68A',           // amber-200
    },
    executed: {
      bg: '#F0FDF4',               // green-50
      text: '#16A34A',             // green-600
      border: '#BBF7D0',           // green-200
    },
    completed: {
      bg: '#F0FDF4',               // green-50
      text: '#16A34A',             // green-600
      border: '#BBF7D0',           // green-200
    },
    replied: {
      bg: '#F5F3FF',               // violet-50
      text: '#7C3AED',             // violet-600
      border: '#DDD6FE',           // violet-200
    },
    stalled: {
      bg: '#FFFBEB',               // amber-50
      text: '#D97706',             // amber-600
      border: '#FDE68A',           // amber-200
    },
    failed: {
      bg: '#FEF2F2',               // red-50
      text: '#DC2626',             // red-600
      border: '#FECACA',           // red-200
    },
    dismissed: {
      bg: '#FEF2F2',               // red-50
      text: '#DC2626',             // red-600
      border: '#FECACA',           // red-200
    },
    // Exclusion statuses (signals filtered before AI evaluation)
    pattern_exclusion: {
      bg: '#FEF3C7',               // amber-100
      text: '#92400E',             // amber-800
      border: '#FDE68A',           // amber-200
    },
    customer_exclusion: {
      bg: '#F3F4F6',               // gray-100
      text: '#4B5563',             // gray-600
      border: '#D1D5DB',           // gray-300
    },
    // Fallback/default
    default: {
      bg: '#F1F5F9',               // slate-100
      text: '#475569',             // slate-600
      border: '#E2E8F0',           // slate-200
    },
  },

  /**
   * Selection/Active State Colors
   *
   * Used for list item selection, active states, hover states.
   */
  selection: {
    // Selected item background
    selectedBg: '#EFF6FF',         // blue-50
    selectedBgStrong: '#DBEAFE',   // blue-100
    // Selected border/indicator
    selectedBorder: '#3B82F6',     // blue-500
    selectedText: '#1D4ED8',       // blue-700
    // Hover states
    hoverBg: '#F8FAFC',            // slate-50
    hoverBgStrong: '#F1F5F9',      // slate-100
  },
} as const;

/**
 * Helper function to get status colors
 * @example getStatusColor('success', 'base') // returns '#10B981'
 */
function getStatusColor(
  variant: 'success' | 'error' | 'warning' | 'info' | 'neutral',
  shade: 'light' | 'lighter' | 'base' | 'dark'
): string {
  return colors.status[variant][shade];
}

/**
 * Helper function to get text color
 * @example getTextColor('primary') // returns '#0F172A'
 */
function getTextColor(
  level: 'primary' | 'secondary' | 'tertiary' | 'quaternary' | 'muted' | 'inverse'
): string {
  return colors.text[level];
}

/**
 * Helper function to get signal type color
 * @example getSignalColor('website_visit') // returns '#3B82F6'
 */
export function getSignalColor(signalType: string): string {
  return (colors.signals as Record<string, string>)[signalType] || colors.signals.default;
}

/**
 * Helper function to get workflow status colors
 * @example getWorkflowColor('in_progress') // returns { bg: '...', text: '...', border: '...' }
 */
export function getWorkflowColor(status: string): { bg: string; text: string; border: string } {
  const workflowColors = colors.workflow as Record<string, { bg: string; text: string; border: string }>;
  return workflowColors[status] || colors.workflow.default;
}

/**
 * Type helpers for better autocompletion
 */
type StatusVariant = 'success' | 'error' | 'warning' | 'info' | 'neutral';
type StatusShade = 'light' | 'lighter' | 'base' | 'dark';
type TextLevel = 'primary' | 'secondary' | 'tertiary' | 'quaternary' | 'muted' | 'inverse';
type SignalType = keyof typeof colors.signals;
type WorkflowStatus = keyof typeof colors.workflow;
